import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Sparkles, ExternalLink } from 'lucide-react';
import { Button, cn } from '@city-sage/aurum-avis-labs-components';
import { TripMap } from '../TripMap';
import { Stop, Day, Leg } from '@city-sage/core-client';
import { ToolCallCard } from '../../lib/hooks/useToolCallHandling';
import { ToolCallIndicatorText } from './ToolCallIndicator';

interface CompactMapPreviewProps {
  stops: Stop[];
  days: Day[];
  legs: Leg[];
  itineraryId?: string;
  title?: string;
  shortDescription?: string;
  toolCall?: ToolCallCard | null;
  onCloseChat?: () => void;
}

export function CompactMapPreview({
  stops,
  days,
  legs,
  itineraryId,
  title,
  shortDescription,
  toolCall,
  onCloseChat,
}: CompactMapPreviewProps) {
  const hasStops = stops.length > 0;
  const showHeader = title || shortDescription || itineraryId || toolCall;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl md:rounded-2xl border border-border overflow-hidden bg-card shadow-sm"
    >
      {/* Compact Header */}
      {showHeader && (
        <div className="px-4 py-3 md:px-5 md:py-4 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Tool call indicator - show first if present */}
              {toolCall && (
                <div className="mb-1.5 md:mb-2">
                  <ToolCallIndicatorText toolCall={toolCall} />
                </div>
              )}
              {/* Title and description */}
              {(title || shortDescription) && (
                <>
                  {title && (
                    <h4 className="font-semibold text-sm md:text-base text-foreground truncate leading-tight">
                      {title}
                    </h4>
                  )}
                  {shortDescription && (
                    <p className="text-xs md:text-sm text-muted-foreground line-clamp-1 mt-1">
                      {shortDescription}
                    </p>
                  )}
                </>
              )}
            </div>
            {itineraryId && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 md:h-9 px-3 md:px-4 text-xs md:text-sm flex-shrink-0"
                asChild
                onClick={onCloseChat}
              >
                <Link to="/itineraries/$itineraryId" params={{ itineraryId }}>
                  View
                  <ExternalLink className="h-3 w-3 md:h-3.5 md:w-3.5 ml-1.5" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Map Container - Compact Height */}
      <div className="relative aspect-[16/9] min-h-[200px] md:min-h-[240px]">
        {hasStops ? (
          <TripMap
            stops={stops}
            days={days}
            legs={legs}
            className="h-full w-full"
            interactive={false}
            hideLegs={false}
            enablePreviewSheet={false}
            itineraryId={itineraryId}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground bg-muted/50">
            <div className="text-center">
              <Sparkles className="h-8 w-8 md:h-10 md:w-10 mx-auto mb-2 md:mb-3 opacity-50" />
              <p className="text-sm md:text-base">Creating itinerary...</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

